import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Flame, ArrowLeft, MessageCircle, Send, Star, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../api/api';
import SEOMeta from '../components/SEOMeta';

const speciesEmoji = { cane: '🐕', gatto: '🐈', coniglio: '🐇', uccello: '🐦', altro: '🐾' };

function formatDateIT(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
}

function CandleFlame({ size = 'md' }) {
  const s = size === 'sm' ? 'w-4 h-7' : 'w-6 h-10';
  const b = size === 'sm' ? 'w-6 h-9' : 'w-8 h-12';
  return (
    <div className="relative flex flex-col items-center">
      <div className={`relative ${s}`}>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-4/5 bg-gradient-to-t from-amber-600 via-amber-400 to-yellow-200 rounded-[50%_50%_40%_40%] candle-flame" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/3 h-2/5 bg-gradient-to-t from-amber-800 to-amber-500 rounded-[50%_50%_30%_30%]" />
      </div>
      <div className={`${b} bg-gradient-to-b from-[#f5f0e8] to-[#e8e0d0] rounded-b-sm border border-[var(--color-border)] candle-glow`} />
    </div>
  );
}

function StoryBlock({ block, index }) {
  const delay = 0.1 + index * 0.1;

  if (block.type === 'intro') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay }}
        className="mb-12"
      >
        <div className="relative pl-6 border-l-4 border-[var(--color-sage)]">
          <div className="absolute -left-3 top-0 w-6 h-6 bg-[var(--color-sage)] rounded-full flex items-center justify-center">
            <Star size={12} className="text-white" fill="currentColor" />
          </div>
          <p className="font-serif text-xl sm:text-2xl italic text-[var(--color-deep)] leading-relaxed">
            {block.text}
          </p>
        </div>
      </motion.div>
    );
  }

  if (block.type === 'section') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay }}
        className="mb-10"
      >
        <h3 className="font-serif text-2xl font-semibold text-[var(--color-deep)] mb-4 flex items-center gap-3">
          <span className="w-8 h-px bg-[var(--color-sage)] inline-block flex-shrink-0" />
          {block.title}
        </h3>
        <p className="text-[var(--color-ink)] leading-loose text-lg">{block.text}</p>
      </motion.div>
    );
  }

  if (block.type === 'quote') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ delay }}
        className="my-14 py-10 px-6 sm:px-12 bg-[var(--color-sage-soft)] border-y-2 border-[var(--color-sage)]/30 text-center"
      >
        <div className="text-6xl text-[var(--color-sage)] font-serif leading-none mb-4 opacity-60">"</div>
        <p className="font-serif text-xl sm:text-2xl italic text-[var(--color-deep)] max-w-2xl mx-auto leading-relaxed">
          {block.text}
        </p>
        {block.author && (
          <p className="text-sm text-[var(--color-muted-text)] mt-5 font-medium tracking-wide uppercase">
            — {block.author}
          </p>
        )}
      </motion.div>
    );
  }

  if (block.type === 'closing') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay }}
        className="mt-12 text-center"
      >
        <div className="flex items-center gap-4 justify-center mb-8">
          <div className="w-16 h-px bg-[var(--color-terracotta)]/40" />
          <Star size={16} className="text-[var(--color-terracotta)]" fill="currentColor" />
          <div className="w-16 h-px bg-[var(--color-terracotta)]/40" />
        </div>
        <p className="font-serif text-lg italic text-[var(--color-muted-text)] leading-relaxed max-w-xl mx-auto">
          {block.text}
        </p>
      </motion.div>
    );
  }

  return null;
}

function Lightbox({ images, startIndex, onClose }) {
  const [current, setCurrent] = useState(startIndex);
  const prev = () => setCurrent(i => (i - 1 + images.length) % images.length);
  const next = () => setCurrent(i => (i + 1) % images.length);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button onClick={onClose} className="absolute top-4 right-4 text-white bg-white/20 rounded-full p-2 hover:bg-white/30 z-10">
        <X size={20} />
      </button>
      <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-4 text-white bg-white/20 rounded-full p-2 hover:bg-white/30 z-10">
        <ChevronLeft size={24} />
      </button>
      <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-4 text-white bg-white/20 rounded-full p-2 hover:bg-white/30 z-10">
        <ChevronRight size={24} />
      </button>
      <div className="max-w-4xl max-h-[85vh] flex flex-col items-center gap-3" onClick={e => e.stopPropagation()}>
        <img
          src={images[current].url}
          alt={images[current].caption}
          className="max-h-[75vh] max-w-full object-contain rounded-lg shadow-2xl"
        />
        {images[current].caption && (
          <p className="text-white/80 text-sm font-medium">{images[current].caption}</p>
        )}
        <p className="text-white/50 text-xs">{current + 1} / {images.length}</p>
      </div>
    </motion.div>
  );
}

export default function GiardinoDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [memorial, setMemorial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [candleDialog, setCandleDialog] = useState(false);
  const [messageDialog, setMessageDialog] = useState(false);
  const [candleForm, setCandleForm] = useState({ lighter_name: '', message: '' });
  const [messageForm, setMessageForm] = useState({ author_name: '', message: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [recentCandles, setRecentCandles] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => { fetchMemorial(); }, [id]);

  const fetchMemorial = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/memorials/${id}`);
      setMemorial(r.data);
    } catch {
      toast.error('Memorial non trovato');
      navigate('/giardino-delle-stelle');
    } finally {
      setLoading(false);
    }
  };

  const handleLightCandle = async (e) => {
    e.preventDefault();
    if (!candleForm.lighter_name) { toast.error('Inserisci il tuo nome'); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/memorials/${id}/candle`, candleForm);
      setMemorial(prev => ({ ...prev, candles: r.data.candles }));
      setRecentCandles(prev => [r.data.candle, ...prev]);
      toast.success('Candela accesa con amore');
      setCandleDialog(false);
      setCandleForm({ lighter_name: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLeaveMessage = async (e) => {
    e.preventDefault();
    if (!messageForm.author_name || !messageForm.message) { toast.error('Compila nome e messaggio'); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/memorials/${id}/message`, messageForm);
      setMemorial(prev => ({ ...prev, messages: [r.data, ...(prev.messages || [])] }));
      toast.success('Messaggio lasciato con affetto');
      setMessageDialog(false);
      setMessageForm({ author_name: '', message: '', email: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#060f09] gap-4">
      <div className="flex flex-col items-center gap-2">
        <CandleFlame />
        <p className="text-white/50 text-sm mt-4 font-serif italic">Caricamento del ricordo…</p>
      </div>
    </div>
  );
  if (!memorial) return null;

  const birthYear = memorial.birth_date ? new Date(memorial.birth_date).getFullYear() : null;
  const deathYear = memorial.death_date ? new Date(memorial.death_date).getFullYear() : null;
  const years = birthYear && deathYear ? deathYear - birthYear : null;
  const gallery = Array.isArray(memorial.gallery) ? memorial.gallery : [];
  const story = Array.isArray(memorial.story) ? memorial.story : [];

  return (
    <div className="bg-[var(--color-cream)] min-h-screen">
      <SEOMeta
        title={`${memorial.pet_name} — Il Giardino delle Stelle`}
        description={memorial.description || `Onora la memoria di ${memorial.pet_name}${memorial.species ? `, ${memorial.species}` : ''}. Accendi una candela e lascia un messaggio d'amore nel Giardino delle Stelle di Una Zampa in Paradiso.`}
        image={memorial.photo_url || undefined}
        url={`/giardino-delle-stelle/${memorial.id}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": `Memorial di ${memorial.pet_name}`,
          "description": memorial.description || `Ricordo di ${memorial.pet_name}`,
          "image": memorial.photo_url || "https://unazampainparadiso.com/logo.png",
          "datePublished": memorial.created_at
        }}
      />
      {/* ===== HERO PARALLAX ===== */}
      <section ref={heroRef} className="relative h-screen overflow-hidden" data-testid="memorial-hero">
        <motion.div className="absolute inset-0" style={{ y: heroY }}>
          <img
            src={memorial.photo_url || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&q=80'}
            alt={memorial.pet_name}
            className="w-full h-full object-cover scale-110"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10" />

        {/* Back button */}
        <button
          onClick={() => navigate('/giardino-delle-stelle')}
          className="absolute top-6 left-6 flex items-center gap-2 text-white/90 bg-white/15 backdrop-blur-sm px-4 py-2 rounded-xl hover:bg-white/25 transition-colors text-sm z-10"
          data-testid="back-to-garden-button"
        >
          <ArrowLeft size={16} /> Giardino delle Stelle
        </button>

        {/* Hero content */}
        <motion.div
          className="absolute bottom-0 left-0 right-0 text-center pb-16 sm:pb-20 px-6 z-10"
          style={{ opacity: heroOpacity }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="text-3xl">{speciesEmoji[memorial.species] || '🐾'}</span>
              <span className="text-white/70 text-sm font-medium uppercase tracking-widest">
                {memorial.breed || memorial.species}
              </span>
            </div>

            <h1 className="font-serif text-5xl sm:text-7xl font-semibold text-white mb-4 drop-shadow-lg">
              {memorial.pet_name}
            </h1>

            {(memorial.birth_date || memorial.death_date) && (
              <div className="flex items-center justify-center gap-3 text-white/70 text-base sm:text-lg mb-6">
                {memorial.birth_date && <span>{new Date(memorial.birth_date).getFullYear()}</span>}
                {memorial.birth_date && memorial.death_date && (
                  <span className="text-white/40 text-2xl">—</span>
                )}
                {memorial.death_date && <span>{new Date(memorial.death_date).getFullYear()}</span>}
                {years && <span className="text-white/50 text-sm ml-2">({years} anni)</span>}
              </div>
            )}

            <div className="flex items-center justify-center gap-6 text-white/60 text-sm">
              {memorial.owner_name && (
                <span className="font-medium">Famiglia {memorial.owner_name}</span>
              )}
              {memorial.city && <span>• {memorial.city}</span>}
            </div>

            <div className="flex items-center justify-center gap-2 mt-6 text-white/60 text-sm">
              <Flame size={14} className="text-amber-400" />
              <span className="font-semibold text-white/90 text-lg">{memorial.candles || 0}</span>
              <span>candele accese</span>
            </div>

            {/* Scroll hint */}
            <div className="mt-8 flex flex-col items-center gap-1 animate-bounce opacity-50">
              <div className="w-px h-8 bg-white/40" />
              <div className="w-1 h-1 bg-white/40 rounded-full" />
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* ===== STORY ARTICLE ===== */}
      {story.length > 0 && (
        <article className="max-w-3xl mx-auto px-6 py-16 sm:py-24" data-testid="memorial-story">
          {story.map((block, index) => (
            <StoryBlock key={index} block={block} index={index} />
          ))}
        </article>
      )}

      {/* ===== DATES & INFO BAR ===== */}
      {!story.length && memorial.description && (
        <div className="max-w-3xl mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl p-8"
          >
            <p className="font-serif text-xl italic text-[var(--color-deep)] leading-relaxed">{memorial.description}</p>
          </motion.div>
        </div>
      )}

      {/* ===== PHOTO GALLERY ===== */}
      {gallery.length > 0 && (
        <section className="py-16 sm:py-20 bg-[var(--color-paper)] border-t border-b border-[var(--color-border)]" data-testid="memorial-gallery">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <div className="inline-flex items-center gap-3 text-[var(--color-terracotta)] text-xs font-bold uppercase tracking-widest mb-3">
                <div className="w-8 h-px bg-current" />
                I suoi momenti
                <div className="w-8 h-px bg-current" />
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl text-[var(--color-deep)]">
                La sua galleria
              </h2>
            </motion.div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
              {gallery.map((photo, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="relative group aspect-square overflow-hidden rounded-xl cursor-pointer shadow-sm"
                  onClick={() => setLightbox(i)}
                  data-testid={`gallery-image-${i}`}
                >
                  <img
                    src={photo.url}
                    alt={photo.caption || memorial.pet_name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                    {photo.caption && (
                      <p className="text-white text-xs font-medium leading-tight">{photo.caption}</p>
                    )}
                  </div>
                  <div className="absolute top-2 right-2 w-7 h-7 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-white text-xs">+</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== CANDLES + MESSAGES ===== */}
      <section className="py-16 sm:py-20 bg-[var(--color-cream)]">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-3 text-[var(--color-terracotta)] text-xs font-bold uppercase tracking-widest mb-3">
              <div className="w-8 h-px bg-current" />
              Lascia un segno
              <div className="w-8 h-px bg-current" />
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[var(--color-deep)]">
              Ricorda {memorial.pet_name}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            {/* Candles card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl p-8 text-center"
            >
              <div className="flex justify-center mb-5">
                <CandleFlame />
              </div>
              <div className="text-4xl font-serif font-semibold text-[var(--color-deep)] mb-1" data-testid="candle-count">
                {memorial.candles || 0}
              </div>
              <p className="text-sm text-[var(--color-muted-text)] mb-6">candele accese per {memorial.pet_name}</p>

              {recentCandles.length > 0 && (
                <div className="space-y-2 mb-6">
                  {recentCandles.slice(0, 3).map(c => (
                    <div key={c.id} className="text-xs text-[var(--color-muted-text)] py-1.5 border-t border-[var(--color-border)] first:border-0">
                      <span className="font-semibold text-[var(--color-deep)]">{c.lighter_name}</span>
                      {c.message && <span className="italic"> — "{c.message}"</span>}
                    </div>
                  ))}
                </div>
              )}

              <Button
                className="w-full bg-[var(--color-terracotta)] hover:bg-[var(--color-terracotta-dark)] text-white rounded-xl btn-press py-5 text-base"
                onClick={() => setCandleDialog(true)}
                data-testid="candle-open-dialog-button"
              >
                <Flame size={18} className="mr-2" /> Accendi una candela
              </Button>
            </motion.div>

            {/* Messages card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl p-8 flex flex-col"
            >
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-serif font-semibold text-[var(--color-deep)] text-xl">Libro dei Ricordi</h3>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-[var(--color-sage)] text-[var(--color-sage)] hover:bg-[var(--color-sage-soft)] text-xs"
                  onClick={() => setMessageDialog(true)}
                  data-testid="leave-message-button"
                >
                  <MessageCircle size={13} className="mr-1.5" /> Scrivi
                </Button>
              </div>

              <div className="flex-1 space-y-4 overflow-y-auto max-h-64">
                {(!memorial.messages || memorial.messages.length === 0) ? (
                  <div className="text-center py-8">
                    <MessageCircle size={32} className="mx-auto text-[var(--color-stone)] mb-3" />
                    <p className="text-[var(--color-muted-text)] text-sm">Sii il primo a lasciare un messaggio</p>
                  </div>
                ) : (
                  memorial.messages.map(msg => (
                    <div key={msg.id} className="border-l-2 border-[var(--color-sage-soft)] pl-4 py-1">
                      <p className="text-sm text-[var(--color-ink)] leading-relaxed italic">"{msg.message}"</p>
                      <p className="text-xs text-[var(--color-muted-text)] mt-1 font-semibold">
                        {msg.author_name} • {new Date(msg.created_at).toLocaleDateString('it-IT')}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== CANDLE DIALOG ===== */}
      <Dialog open={candleDialog} onOpenChange={setCandleDialog}>
        <DialogContent className="bg-[var(--color-paper)] border-[var(--color-border)] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-[var(--color-deep)] text-xl">
              Accendi una candela per {memorial.pet_name}
            </DialogTitle>
            <DialogDescription className="text-[var(--color-muted-text)] text-sm">
              La tua luce brilla in questo giardino per sempre.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center my-4">
            <CandleFlame />
          </div>
          <form onSubmit={handleLightCandle} className="space-y-4">
            <div>
              <Label className="text-[var(--color-deep)] font-medium">Il tuo nome *</Label>
              <Input
                value={candleForm.lighter_name}
                onChange={e => setCandleForm(f => ({ ...f, lighter_name: e.target.value }))}
                placeholder="Il tuo nome"
                className="mt-1.5 border-[var(--color-border)]"
                data-testid="candle-name-input"
              />
            </div>
            <div>
              <Label className="text-[var(--color-deep)] font-medium">Un pensiero (opzionale)</Label>
              <Textarea
                value={candleForm.message}
                onChange={e => setCandleForm(f => ({ ...f, message: e.target.value }))}
                placeholder={`Un pensiero per ${memorial.pet_name}...`}
                className="mt-1.5 border-[var(--color-border)] resize-none"
                rows={3}
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[var(--color-terracotta)] hover:bg-[var(--color-terracotta-dark)] text-white rounded-xl py-5 btn-press"
              data-testid="candle-submit-button"
            >
              <Flame size={16} className="mr-2" /> Accendi la candela
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== MESSAGE DIALOG ===== */}
      <Dialog open={messageDialog} onOpenChange={setMessageDialog}>
        <DialogContent className="bg-[var(--color-paper)] border-[var(--color-border)] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-[var(--color-deep)] text-xl">
              Lascia un messaggio nel Libro dei Ricordi
            </DialogTitle>
            <DialogDescription className="text-[var(--color-muted-text)] text-sm">
              Le tue parole rimarranno qui per sempre, vicino a {memorial?.pet_name}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLeaveMessage} className="space-y-4">
            <div>
              <Label className="text-[var(--color-deep)] font-medium">Il tuo nome *</Label>
              <Input
                value={messageForm.author_name}
                onChange={e => setMessageForm(f => ({ ...f, author_name: e.target.value }))}
                placeholder="Il tuo nome"
                className="mt-1.5 border-[var(--color-border)]"
                data-testid="message-author-name"
              />
            </div>
            <div>
              <Label className="text-[var(--color-deep)] font-medium">La tua email (opzionale)</Label>
              <Input
                type="email"
                value={messageForm.email}
                onChange={e => setMessageForm(f => ({ ...f, email: e.target.value }))}
                placeholder="email@esempio.it"
                className="mt-1.5 border-[var(--color-border)]"
              />
            </div>
            <div>
              <Label className="text-[var(--color-deep)] font-medium">Il tuo messaggio *</Label>
              <Textarea
                value={messageForm.message}
                onChange={e => setMessageForm(f => ({ ...f, message: e.target.value }))}
                placeholder={`Scrivi un pensiero per ${memorial.pet_name}...`}
                className="mt-1.5 border-[var(--color-border)] resize-none"
                rows={4}
                data-testid="message-content"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[var(--color-sage)] hover:bg-[var(--color-sage-dark)] text-white rounded-xl py-5"
              data-testid="message-submit-button"
            >
              <Send size={16} className="mr-2" /> Lascia il messaggio
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== LIGHTBOX ===== */}
      {lightbox !== null && (
        <Lightbox
          images={gallery}
          startIndex={lightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
